"use client";

import { BABY_NAMES_HEADLINES } from "@/lib/baby-names-answer";

import { useState, type ReactNode } from "react";
import { suggestBabyNames } from "@/lib/api/baby-names";
import type { BabyNameResponse } from "@/lib/api/baby-names";
import type { KundaliRequest } from "@ishvaram/api-client";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { BabyNamesForm, BabyNamesResult, type BabyNameGenderFilter } from "@/components/baby-names";

export default function BabyNamesClient({
  language = "en",
  resultOffer,
  crossLinks,
}: {
  language?: "en" | "hi";
  resultOffer: ReactNode;
  crossLinks: ReactNode;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BabyNameResponse | null>(null);
  const [genderFilter, setGenderFilter] = useState<BabyNameGenderFilter>("all");

  async function handleBirthSubmit(data: KundaliRequest) {
    if (!data.birth_time || data.latitude == null || data.longitude == null || !data.timezone) {
      setError("Enter an exact birth time and select the birth place from the suggestions.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await suggestBabyNames({
        mode: "birth",
        birth_date: data.birth_date,
        birth_time: data.birth_time,
        birth_place: data.birth_place,
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone,
        gender: genderFilter !== "all" ? genderFilter : undefined,
        limit: 200,
      });
      setResult(response);
      trackCalculatorUsed({ calculator: "baby-names" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function handleNakshatraSubmit(data: {
    nakshatra: string;
    pada?: number;
    birthDate: string;
  }) {
    if (!data.nakshatra || !data.birthDate) {
      setError("Please select a nakshatra and enter birth date.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await suggestBabyNames({
        mode: "nakshatra",
        birth_date: data.birthDate,
        nakshatra: data.nakshatra,
        pada: data.pada,
        gender: genderFilter !== "all" ? genderFilter : undefined,
        limit: 200,
      });
      setResult(response);
      trackCalculatorUsed({ calculator: "baby-names" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]" data-design-owner="baby-names-client">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <BabyNamesHeader language={language} />

        {/* Input Section */}
        {!result ? (
          <BabyNamesForm
            loading={loading}
            error={error}
            onBirthSubmit={handleBirthSubmit}
            onNakshatraSubmit={handleNakshatraSubmit}
            genderFilter={genderFilter}
            onGenderFilterChange={setGenderFilter}
          />
        ) : (
          <BabyNamesResult
            result={result}
            resultOffer={resultOffer}
            onReset={() => {
              setResult(null);
              setError(null);
            }}
          />
        )}

        {crossLinks}
      </div>
    </div>
  );
}

function BabyNamesHeader({ language }: { language: "en" | "hi" }) {
  return (
    <div className="text-center mb-10">
      <p className="mb-3 text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        Naam Karan
      </p>
      <h1 className="font-serif text-headline font-bold leading-tight text-text-primary sm:text-display">
        {BABY_NAMES_HEADLINES[language]}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-body leading-relaxed text-text-secondary">
        Find the perfect name for your child &mdash; filtered by birth star and scored by numerology
        compatibility.
      </p>

      <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-caption font-medium text-text-muted">
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          1500+ Names
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          Instant Results
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          100% Free
        </span>
      </div>
    </div>
  );
}
